// ========================================
// Quick Schedule Form
// Handles quick class scheduling from calendar
// ========================================

const QuickScheduleForm = {
    /**
     * Show quick schedule form
     */
    showQuickScheduleForm(info, onSuccess) {
        const clickedDate = info.date || info.start;
        const clickedTime = clickedDate.toTimeString().slice(0, 5);
        const dayOfWeek = clickedDate.getDay();

        // Calculate end time (1 hour later)
        const startHour = parseInt(clickedTime.split(':')[0]);
        const endTime = `${String(startHour + 1).padStart(2, '0')}:00`;

        const formations = FormationService.getFormations();

        const content = `
            <form id="quick-schedule-form">
                <div class="form-group">
                    <label class="form-label">Formación *</label>
                    <select class="form-select" id="quick-formation" required>
                        <option value="">Seleccionar formación...</option>
                        ${formations.map(f => `
                            <option value="${Utils.sanitizeHTML(f.id)}">${Utils.sanitizeHTML(f.name)}</option>
                        `).join('')}
                    </select>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Asignatura *</label>
                    <select class="form-select" id="quick-subject" required disabled>
                        <option value="">Primero selecciona una formación</option>
                    </select>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Días de la semana *</label>
                    <div class="day-selector">
                        ${[1, 2, 3, 4, 5, 6, 0].map(day => `
                            <label class="day-checkbox">
                                <input type="checkbox" name="days" value="${day}" ${day === dayOfWeek ? 'checked' : ''}>
                                <span class="day-badge">${Utils.getDayName(day).charAt(0)}</span>
                            </label>
                        `).join('')}
                    </div>
                    <small style="color: var(--text-secondary); display: block; margin-top: 0.5rem;">
                        Selecciona uno o más días para programar la misma clase
                    </small>
                </div>
                
                <div class="grid grid-2">
                    <div class="form-group">
                        <label class="form-label">Hora inicio *</label>
                        <input type="time" step="900" class="form-input" id="quick-start-time" required value="${clickedTime}">
                    </div>
                    <div class="form-group">
                        <label class="form-label">Hora fin *</label>
                        <input type="time" step="900" class="form-input" id="quick-end-time" required value="${endTime}">
                    </div>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Aula</label>
                    <input type="text" class="form-input" id="quick-classroom" placeholder="Ej: Aula 101">
                </div>
                
                <div class="grid grid-2">
                    <div class="form-group">
                        <label class="form-label">Fecha inicio *</label>
                        <input type="date" class="form-input" id="quick-start-date" 
                               value="${clickedDate.toISOString().split('T')[0]}" required>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Fecha fin</label>
                        <input type="date" class="form-input" id="quick-end-date">
                    </div>
                </div>
                
                <div id="quick-conflicts" style="display: none;"></div>
                
                <div class="flex gap-1" style="margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" id="quick-cancel">
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                        <i class="fas fa-calendar-plus"></i> Programar
                    </button>
                </div>
            </form>
        `;

        ModalManager.openModal('Programación Rápida', content);

        // Setup event listeners
        document.getElementById('quick-formation').addEventListener('change', () => {
            this.updateSubjectOptions();
        });

        document.getElementById('quick-cancel').addEventListener('click', () => {
            ModalManager.closeModal();
        });

        document.getElementById('quick-schedule-form').addEventListener('submit', (e) => {
            this.handleQuickSchedule(e, onSuccess);
        });
    },

    /**
     * Update subject options based on selected formation
     */
    updateSubjectOptions() {
        const formationId = document.getElementById('quick-formation').value;
        const subjectSelect = document.getElementById('quick-subject');

        if (!formationId) {
            SafeHTML.set(subjectSelect, '<option value="">Primero selecciona una formación</option>');
            subjectSelect.disabled = true;
            return;
        }

        const subjects = SubjectService.getSubjectsByFormation(formationId);

        if (subjects.length === 0) {
            SafeHTML.set(subjectSelect, '<option value="">No hay asignaturas en esta formación</option>');
            subjectSelect.disabled = true;
            return;
        }

        subjectSelect.disabled = false;
        SafeHTML.set(subjectSelect, '<option value="">Seleccionar asignatura...</option>' +
            subjects.map(s => {
                const professor = ProfessorService.getProfessor(s.professorId);
                const professorName = professor ? `${professor.firstName} ${professor.lastName}` : 'Sin profesor';
                return `<option value="${Utils.sanitizeHTML(s.id)}">${Utils.sanitizeHTML(s.name)} (${Utils.sanitizeHTML(professorName)})</option>`;
            }).join(''));
    },

    /**
     * Handle quick schedule form submission
     */
    handleQuickSchedule(event, onSuccess) {
        event.preventDefault();

        const formationId = document.getElementById('quick-formation').value;
        const subjectId = document.getElementById('quick-subject').value;
        const startTime = document.getElementById('quick-start-time').value;
        const endTime = document.getElementById('quick-end-time').value;
        const classroom = document.getElementById('quick-classroom').value;
        const startDate = document.getElementById('quick-start-date').value;
        const endDate = document.getElementById('quick-end-date').value;

        // Get selected days
        const selectedDays = Array.from(document.querySelectorAll('input[name="days"]:checked'))
            .map(cb => parseInt(cb.value));

        if (selectedDays.length === 0) {
            ToastManager.showError('Debes seleccionar al menos un día');
            return;
        }

        // Validate times
        if (endTime <= startTime) {
            ToastManager.showError('La hora de fin debe ser posterior a la hora de inicio');
            return;
        }

        const subject = SubjectService.getSubject(subjectId);
        if (!subject) {
            ToastManager.showError('Error: Asignatura no encontrada');
            return;
        }

        // Check conflicts for each day
        const conflicts = [];
        const success = [];

        selectedDays.forEach(dayOfWeek => {
            const conflict = ConflictService.checkProfessorConflict(
                subject.professorId,
                dayOfWeek,
                startTime,
                endTime
            );

            if (conflict.conflict) {
                const conflictSubject = SubjectService.getSubject(conflict.class.subjectId);
                const conflictFormation = FormationService.getFormation(conflict.class.formationId);
                conflicts.push({
                    day: dayOfWeek,
                    dayName: Utils.getDayName(dayOfWeek),
                    subjectName: conflictSubject ? conflictSubject.name : 'Desconocido',
                    formationName: conflictFormation ? conflictFormation.name : 'Desconocido',
                    time: `${conflict.class.startTime} - ${conflict.class.endTime}`
                });
            } else {
                success.push(dayOfWeek);
            }
        });

        // If there are conflicts, show warning
        if (conflicts.length > 0) {
            this.showConflictWarning(conflicts, success, {
                formationId,
                subjectId,
                professorId: subject.professorId,
                startTime,
                endTime,
                classroom,
                startDate,
                endDate
            }, onSuccess);
            return;
        }

        // Create classes for all selected days
        this.createClassesForDays(selectedDays, {
            formationId,
            subjectId,
            professorId: subject.professorId,
            startTime,
            endTime,
            classroom,
            startDate,
            endDate
        }, onSuccess);
    },

    /**
     * Show conflict warning
     */
    showConflictWarning(conflicts, successDays, formData, onSuccess) {
        const conflictsDiv = document.getElementById('quick-conflicts');

        let html = '<div class="conflict-warning">';
        html += '<h4 style="margin-bottom: 0.75rem; color: var(--error-color);"><i class="fas fa-exclamation-triangle"></i> Conflictos Detectados</h4>';

        conflicts.forEach(c => {
            html += `
                <div class="conflict-item error">
                    <i class="fas fa-times-circle"></i>
                    <strong>${c.dayName}:</strong> Conflicto con "${c.subjectName}" (${Utils.sanitizeHTML(c.formationName)}) a las ${c.time}
                </div>
            `;
        });

        if (successDays.length > 0) {
            html += '<h4 style="margin-top: 1rem; margin-bottom: 0.75rem; color: var(--success-color);">Días Disponibles</h4>';
            successDays.forEach(day => {
                html += `
                    <div class="conflict-item success">
                        <i class="fas fa-check-circle"></i>
                        <strong>${Utils.getDayName(day)}:</strong> Disponible
                    </div>
                `;
            });

            html += `
                <div style="margin-top: 1rem;">
                    <button type="button" class="btn btn-primary" style="width: 100%;" 
                            id="proceed-available-days">
                        <i class="fas fa-calendar-check"></i> Programar solo en días disponibles (${successDays.length})
                    </button>
                </div>
            `;
        }

        html += '</div>';

        SafeHTML.set(conflictsDiv, html);
        conflictsDiv.style.display = 'block';

        // Add event listener for proceed button
        const proceedBtn = document.getElementById('proceed-available-days');
        if (proceedBtn) {
            proceedBtn.addEventListener('click', () => {
                this.createClassesForDays(successDays, formData, onSuccess);
            });
        }
    },

    /**
     * Create classes for specified days
     */
    createClassesForDays(days, formData, onSuccess) {
        let created = 0;
        days.forEach(dayOfWeek => {
            ClassService.createClass({
                subjectId: formData.subjectId,
                formationId: formData.formationId,
                professorId: formData.professorId,
                dayOfWeek: dayOfWeek,
                startTime: formData.startTime,
                endTime: formData.endTime,
                classroom: formData.classroom,
                startDate: formData.startDate,
                endDate: formData.endDate || null
            });
            created++;
        });

        ToastManager.showSuccess(
            `${created} clase${created > 1 ? 's' : ''} programada${created > 1 ? 's' : ''} correctamente`
        );

        ModalManager.closeModal();

        // Call success callback
        if (onSuccess) {
            onSuccess();
        }
    }
};

// Make QuickScheduleForm globally available
window.QuickScheduleForm = QuickScheduleForm;
